import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo, aboutSEO, siteInfo } from '@/mocks/photography';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于秀人写真',
  description: siteInfo.description,
  url: window.location.origin + (window.__BASE_PATH__ || '') + '/about',
  about: {
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    foundingDate: '2018',
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座1208室',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  },
};

export default function About() {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <SEOHead data={aboutSEO} />
      <ScrollToTop />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[350px] md:h-[420px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20photography%20studio%20interior%2C%20professional%20camera%20equipment%20on%20display%2C%20warm%20natural%20light%20from%20large%20windows%2C%20cream%20walls%2C%20wooden%20floors%2C%20artistic%20atmosphere%2C%20clean%20modern%20aesthetic%2C%20soft%20shadows&width=1600&height=700&seq=about-hero-01&orientation=landscape"
            alt="秀人写真工作室"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/50"></div>
          <div className="absolute z-10 text-center px-6">
            <h1 className="text-2xl md:text-3xl font-heading text-white mb-3">关于秀人写真</h1>
            <p className="text-white/70 text-sm max-w-md mx-auto">用镜头发现美，用影像记录爱</p>
          </div>
        </section>

        <div className="px-6 md:px-10 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Stats */}
        <section className="py-12 px-6 md:px-10 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading text-primary-500">{stat.number}</div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-18 px-6 md:px-10 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-5">品牌故事</h2>
                {aboutInfo.brandStory.split('\n\n').map((para, i) => (
                  <p key={i} className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20photography%20team%20group%20portrait%20in%20modern%20studio%2C%20warm%20natural%20light%2C%20creative%20professionals%2C%20clean%20cream%20interior%2C%20minimalist%20aesthetic%2C%20team%20spirit%2C%20soft%20shadows&width=600&height=400&seq=about-brand-01&orientation=landscape"
                  alt="秀人写真团队"
                  className="w-full h-auto"
                  title="秀人写真专业摄影团队"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="py-14 md:py-18 px-6 md:px-10 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-8 text-center">服务理念</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutInfo.philosophy.map((item) => (
                <div key={item.title} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-star-line text-primary-500 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-18 px-6 md:px-10 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-8 text-center">专业团队</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {aboutInfo.teamPhotos.map((photo, i) => (
                <div key={i} className="rounded-xl overflow-hidden bg-background-100 border border-background-200/70">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={photo}
                      alt={`秀人写真团队成员 ${i + 1}`}
                      className="w-full h-full object-cover"
                      title="秀人写真专业摄影团队"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-14 md:py-18 px-6 md:px-10 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-primary-500"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">电话</p>
                <p className="text-sm font-medium text-foreground-950">{siteInfo.phone}</p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-500"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">邮箱</p>
                <p className="text-sm font-medium text-foreground-950 break-all">{siteInfo.email}</p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-primary-500"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">地址</p>
                <p className="text-sm font-medium text-foreground-950 leading-relaxed">{siteInfo.address}</p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-primary-500"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">营业时间</p>
                <p className="text-sm font-medium text-foreground-950">{siteInfo.workingHours}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}